import { UseQueryOptions } from "@tanstack/react-query";
import { client, UserDetailsDto, UserMinimalDto } from "@/client";
import {
  ChipLookup,
  LookupFilterChipValue,
} from "@/components/custom/data-table/lookup-filter-chip-value";
import { ChipRow, chipIds } from "@/lib/filter-chip-label";
import { listPermissions, PermissionCheck } from "@/lib/list-permissions";

// `filterBreadcrumbs` formatters for the id filter keys, each naming its ids from the list of the
// entity they point at (ID-7084). The same key means different things on different tables --
// `tagIds` is device, sample, site or catalog tags -- so every table context picks its formatter.
//
// Most filter controls page through a searched list ten rows at a time, so a chip can't share their
// cache entries. It loads the whole list once instead, as the JSON-data, Pending and tag filters
// already do; where a control loads that same full list, the query keys match and the two share one
// entry. The minimal endpoints take no `ids` filter, so a list is cut at 9999 rows, and which rows
// make the cut is the endpoint's own order -- catalog labels have none. An id the list doesn't hold
// reads "#<id>", like a hidden, merged or other-account entity the list leaves out.

const FULL_LIST = { page: 1, limit: 9999 };

// Names change only through dashboard edits, and those invalidate these queries anyway, so a chip
// has no need to refetch its whole list each time the tab regains focus.
const LOOKUP_STALE_TIME = 5 * 60 * 1000;

/** Pairs a query with the rows it names, typing `rows` against the query's data. */
export function chipLookup<TQueryFnData, TData = TQueryFnData>({
  query,
  rows,
  permission,
  staleTime = LOOKUP_STALE_TIME,
}: {
  query: UseQueryOptions<TQueryFnData, any, TData, any>;
  rows: (data: TData) => readonly ChipRow[] | undefined;
  permission: PermissionCheck;
  /** How long the chip's copy of the list stays fresh; five minutes unless set. */
  staleTime?: number;
}): ChipLookup {
  return {
    // A chip falls back to "#<id>" when its lookup fails, so the failure needs no toast of its own.
    query: { ...query, staleTime, meta: { ...query.meta, hideToast: true } },
    rows,
    permission,
  };
}

/**
 * A `filterBreadcrumbs` formatter naming an id filter's values from `lookups`. A formatter replaces
 * DataTableActiveFilters' own rendering outright, so an empty filter has to come back as "" from
 * here -- that is what drops the chip.
 */
export function lookupChipFormatter(...lookups: ChipLookup[]) {
  return (value: unknown) => {
    const ids = chipIds(value);
    return ids.length > 0 ? (
      <LookupFilterChipValue ids={ids} lookups={lookups} />
    ) : (
      ""
    );
  };
}

// A user's name as the Assignee and Tagged-by filters show it.
const fullName = (user: { firstName?: string; lastName?: string }) =>
  [user.firstName, user.lastName].filter(Boolean).join(" ");

const sites = chipLookup({
  query: client.sites.listMinimal.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.sites,
});
const siteTags = chipLookup({
  query: client.sites.listTags.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.siteTags,
});
// GET /sites/regions and /sites/subRegions (ID-6524): the option lists behind the Data Review
// region filters, read whole for the chips -- without its regionId filter the sub-region list is
// account-wide. An inactivated region is deliberately absent from both lists and reads "#<id>".
const regions = chipLookup({
  query: client.sites.getRegions.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.regions,
});
const subRegions = chipLookup({
  query: client.sites.getSubRegions.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.regions,
});
const clusters = chipLookup({
  query: client.clusters.listMinimal.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.clusters,
});
const devices = chipLookup({
  query: client.devices.listMinimal.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.devices,
});
const models = chipLookup({
  query: client.models.listMinimal.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.models,
});
const siteGroups = chipLookup({
  query: client.siteGroups.listSummaries.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.siteGroups,
});
const sampleTags = chipLookup({
  query: client.tags.listSummaries.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.sampleTags,
});
const sessionTags = chipLookup({
  query: client.sessions.getTags.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.sessionTags,
});
const catalogTags = chipLookup({
  query: client.catalog.getTags.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.catalogTags,
});
// The catalog's Label filter lists the items by PLU, which the endpoint hands over as `name`.
const catalogLabels = chipLookup({
  query: client.catalog.getLabels.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.catalogLabels,
});
// The generated paginated types intersect `results` with `unknown[]`, so the row type is spelled out.
const users = chipLookup({
  query: client.users.listMinimal.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) =>
    data.results?.map((user: UserMinimalDto) => ({
      id: user.id,
      name: fullName(user),
    })),
  permission: listPermissions.users,
});
// The Tagged-by filter's own query, so the two share one cache entry.
const userDetails = chipLookup({
  query: client.users.getAllUsers.asQueryOptions(FULL_LIST),
  rows: (data) =>
    data.results?.map((user: UserDetailsDto) => ({
      id: user.id,
      name: fullName(user),
    })),
  permission: listPermissions.userDetails,
});
// The dashboard and admin routes load /me once and never let it go stale; the chips keep it so.
const meQuery = client.users.me.asQueryOptions();
// The Assignee filter offers the signed-in user first, who need not be one of the account's users.
const me = chipLookup({
  query: meQuery,
  rows: (user) => [{ id: user.id, name: fullName(user) }],
  permission: listPermissions.me,
  staleTime: Infinity,
});
// The Manager report's Sites filter offers the user's own sites, and its route seeds the first one.
const associatedSites = chipLookup({
  query: meQuery,
  rows: (user) => user.associatedSites,
  permission: listPermissions.me,
  staleTime: Infinity,
});
const accounts = chipLookup({
  query: client.accounts.listMinimal.assign(FULL_LIST).asQueryOptions({}),
  rows: (data) => data.results,
  permission: listPermissions.accounts,
});
// The Dataset filter's own query; this endpoint is not paginated.
const datasets = chipLookup({
  query: client.staticDatasets.listMinimal.asQueryOptions(),
  rows: (data) => data,
  permission: listPermissions.datasets,
});

export const formatSiteIdsChip = lookupChipFormatter(sites);
export const formatSiteTagIdsChip = lookupChipFormatter(siteTags);
export const formatRegionIdChip = lookupChipFormatter(regions);
export const formatSubRegionIdChip = lookupChipFormatter(subRegions);
export const formatClusterIdsChip = lookupChipFormatter(clusters);
export const formatDeviceIdsChip = lookupChipFormatter(devices);
export const formatModelIdsChip = lookupChipFormatter(models);
export const formatSiteGroupIdsChip = lookupChipFormatter(siteGroups);
export const formatSampleTagIdsChip = lookupChipFormatter(sampleTags);
export const formatSessionTagIdsChip = lookupChipFormatter(sessionTags);
export const formatCatalogTagIdsChip = lookupChipFormatter(catalogTags);
export const formatCatalogItemIdsChip = lookupChipFormatter(catalogLabels);
export const formatAssigneeIdsChip = lookupChipFormatter(users, me);
export const formatTaggedByIdsChip = lookupChipFormatter(userDetails);
export const formatAssociatedSiteIdsChip = lookupChipFormatter(associatedSites);
export const formatAccountIdsChip = lookupChipFormatter(accounts);

const formatDatasetIds = lookupChipFormatter(datasets);
/** `datasetId` holds one dataset; -1 is Production, the default, which gets no chip. */
export const formatDatasetIdChip = (value: unknown) =>
  String(value) === "-1" ? "" : formatDatasetIds(value);
